import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type EStockFilter,
  STOCK_FILTER_DEFAULT,
  STOCK_FILTER_OPTIONS,
} from '../../reducers/products/types';

const LABEL_KEYS: Record<EStockFilter, string> = {
  all: 'products.filter.stock.all',
  inStock: 'products.filter.stock.inStock',
  lowStock: 'products.filter.stock.lowStock',
  outOfStock: 'products.filter.stock.outOfStock',
};

const DEFAULT_LABELS: Record<EStockFilter, string> = {
  all: 'All',
  inStock: 'In stock',
  lowStock: 'Last pieces',
  outOfStock: 'Out of stock',
};

export function hasVisibleStockOptions(
  availableOptions: EStockFilter[] | null,
  value: EStockFilter,
): boolean {
  return !(
    (availableOptions?.length ?? 0) <= 1 && value === STOCK_FILTER_DEFAULT
  );
}

function StockQuickFilter({
  value,
  availableOptions,
  onChange,
}: {
  value: EStockFilter;
  availableOptions: EStockFilter[] | null;
  onChange: (next: EStockFilter) => void;
}): ReactElement | null {
  const { t } = useTranslation();

  if (!hasVisibleStockOptions(availableOptions, value)) {
    return null;
  }

  const visibleOptions = STOCK_FILTER_OPTIONS.filter(
    (option: EStockFilter): boolean =>
      option === STOCK_FILTER_DEFAULT ||
      value === option ||
      Boolean(availableOptions?.includes(option)),
  );

  return (
    <div className="pf-group">
      <p className="pf-group__label">
        {t('products.filter.stock.label', { defaultValue: 'Availability' })}
      </p>
      <div className="pf-chips">
        {visibleOptions.map((option: EStockFilter): ReactElement => (
          <button
            key={option}
            type="button"
            className={`pf-chip${value === option ? ' pf-chip--active' : ''}`}
            aria-pressed={value === option}
            onClick={(): void => onChange(option)}
          >
            {t(LABEL_KEYS[option], { defaultValue: DEFAULT_LABELS[option] })}
          </button>
        ))}
      </div>
    </div>
  );
}

export default StockQuickFilter;
